import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { CommonService } from '../../../api/services/common.service';
import { FORM_ID } from '../../../helpers/form-id.enum';

export interface UserInviteItem {
  id: string | number;
  name: string;
  email: string;
  initials?: string;
  status?: string;
  avatarBg?: string;
  profilePhoto?: string | null;
  role?: any;
  roleObject?: any;
}

@Component({
  selector: 'app-user-invite',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './user-invite.component.html',
  styleUrl: './user-invite.component.css',
})
export class UserInviteComponent implements OnInit {
  @Input() formGroup: any;
  @Input() fields: any;
  users: UserInviteItem[] = [];

  isRoleDropdownOpen: boolean = false;
  activeRoleDropdownUserId: string | number | null = null;
  dropdownDirectionMap: Map<string | number, boolean> = new Map();

  statusFilters: string[] = ['All Users', 'Active', 'Inactive', 'Invited'];
  selectedStatusFilter: string = 'All Users';
  isStatusFilterOpen: boolean = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.role-dropdown-container')) {
      this.isRoleDropdownOpen = false;
    }
    if (!target.closest('.row-role-dropdown-container')) {
      this.activeRoleDropdownUserId = null;
    }
    if (!target.closest('.status-filter-container')) {
      this.isStatusFilterOpen = false;
    }
  }

  onModalClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.role-dropdown-container')) {
      this.isRoleDropdownOpen = false;
    }
    if (!target.closest('.row-role-dropdown-container')) {
      this.activeRoleDropdownUserId = null;
    }
    if (!target.closest('.status-filter-container')) {
      this.isStatusFilterOpen = false;
    }
  }

  @Input() initialSelectedUserIds: (string | number)[] = [];
  private _organizationName: string = 'Acme Corporation';

  @Input()
  set organizationName(value: string) {
    if (value) {
      this._organizationName = value;
      this.message = `You have been invited to join ${value} on eCISO platform.`;
    }
  }

  get organizationName(): string {
    return this._organizationName;
  }

  @Input() roles: string[] = ['SecOps', 'Admin', 'Analyst', 'Viewer', 'Member', 'Owner'];
  @Input() selectedRole: string = 'SecOps';
  @Input() message: string =
    'You have been invited to join Acme Corporation on eCISO platform.';
  @Input() maxMessageLength: number = 200;
  @Input() infoMessage: string =
    'An invitation email will be sent to all selected users with instructions to activate their account.';

  @Output() selectionChange = new EventEmitter<UserInviteItem[]>();
  @Output() sendInvitation = new EventEmitter<{
    selectedUsers: UserInviteItem[];
    role: any;
  }>();
  @Output() clearSelectionEvent = new EventEmitter<void>();
  @Output() closeModal = new EventEmitter<void>();

  inviteWithBulk: boolean = true;
  searchTerm: string = '';
  selectedUserIds: Set<string | number> = new Set();
  selectedUserMap: Map<string | number, UserInviteItem> = new Map();
  userRolesMap: Map<string | number, string> = new Map();
  userRolesObjectMap: Map<string | number, any> = new Map();
  selectAllAcrossPages: boolean = false;
  roleSearchQuery: string = '';
  roleError: boolean = false;
  roleRecords: any[] = [];
  selectedRoleObject: any = null;

  get filteredRoles(): string[] {
    if (!this.roleSearchQuery || !this.roleSearchQuery.trim()) {
      return this.roles;
    }
    const term = this.roleSearchQuery.trim().toLowerCase();
    return this.roles.filter((role: string) =>
      role.toLowerCase().includes(term),
    );
  }

  getSecOpsRole(): { role: string; roleObject: any } {
    const record = this.roleRecords.find((r: any) => {
      const name = (r.role || r.name || r.roleName || '').toString().toLowerCase();
      return name === 'secops';
    });
    if (record) {
      const roleName = record.role || record.name || record.roleName || 'SecOps';
      return { role: roleName, roleObject: record };
    }

    const foundStr = this.roles.find((r: string) => r.toLowerCase() === 'secops');
    if (foundStr) {
      return { role: foundStr, roleObject: null };
    }

    return { role: 'SecOps', roleObject: null };
  }

  currentPage: number = 1;
  limit: number = 10;
  totalRecords: number = 0;
  isSearching: boolean = false;
  isLoadingMore: boolean = false;
  hasMoreUsers: boolean = true;
  private searchTimeout: any;

  // Role Pagination
  rolePage: number = 1;
  roleLimit: number = 20;
  hasMoreRoles: boolean = true;
  isLoadingRoles: boolean = false;

  constructor(
    private commonService: CommonService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.initialSelectedUserIds && this.initialSelectedUserIds.length > 0) {
      this.initialSelectedUserIds.forEach((id) => this.selectedUserIds.add(id));
    }
    this.isSearching = true;
    this.loadUsersFromApi(false);
    this.loadRolesFromApi();
  }

  loadRolesFromApi(append: boolean = false): void {
    if (this.isLoadingRoles) return;
    if (append && !this.hasMoreRoles) return;

    this.isLoadingRoles = true;
    if (!append) {
      this.rolePage = 1;
      this.hasMoreRoles = true;
      this.roles = [];
      this.roleRecords = [];
    }

    const options = {
      roleStatus: '=="Active"',
      page: this.rolePage,
      limit: this.roleLimit,
    };

    this.commonService
      .loadListWithOptions('6a0ed7f32db4b8fda5f347a6', options)
      .subscribe({
        next: (res: any) => {
          const records = res?.data?.records || [];
          if (append) {
            this.roleRecords = [...this.roleRecords, ...records];
          } else {
            this.roleRecords = records;
          }

          const fetchedRoles = records
            .map((r: any) => r.role || r.name || r.roleName)
            .filter(Boolean);

          if (append) {
            this.roles = [...this.roles, ...fetchedRoles];
          } else {
            this.roles =
              fetchedRoles.length > 0
                ? fetchedRoles
                : ['SecOps', 'Admin', 'Analyst', 'Viewer', 'Member', 'Owner'];
          }

          if (!this.roles.some((r: string) => r.toLowerCase() === 'secops')) {
            this.roles.unshift('SecOps');
          }

          // Backfill roleObject for any currently selected users
          const secOps = this.getSecOpsRole();
          this.selectedUsers.forEach((u) => {
            if (!u.roleObject && u.role) {
              const found =
                this.roleRecords.find(
                  (r: any) => (r.role || r.name || r.roleName) === u.role,
                ) || (u.role.toLowerCase() === 'secops' ? secOps.roleObject : null);
              if (found) {
                u.roleObject = found;
                this.userRolesObjectMap.set(u.id, found);
                const inList = this.users.find((user) => user.id === u.id);
                if (inList) inList.roleObject = found;
                const inMap = this.selectedUserMap.get(u.id);
                if (inMap) inMap.roleObject = found;
              }
            }
          });

          const totalCount = res?.data?.totalCount || 0;

          if (
            fetchedRoles.length === 0 ||
            fetchedRoles.length < this.roleLimit
          ) {
            this.hasMoreRoles = false;
          } else {
            this.hasMoreRoles = this.roles.length < totalCount;
          }

          this.isLoadingRoles = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error fetching roles:', err);
          this.roles = ['SecOps', 'Admin', 'Analyst', 'Viewer', 'Member', 'Owner'];
          this.isLoadingRoles = false;
          this.cdr.detectChanges();
        },
      });
  }

  loadUsersFromApi(append: boolean = false): void {
    if (!append) {
      this.currentPage = 1;
      this.hasMoreUsers = true;
      this.isSearching = true;
      this.users = [];
    }

    const options: any = {
      page: this.currentPage,
      limit: this.limit,
      status: ['IN-ACTIVE'],
      contactType: ['Primary', 'Others'],
      userType: 'Member',
      sort: 'email',
    };

    if (this.searchTerm.trim()) {
      options.query = this.searchTerm.trim();
    }

    this.commonService.loadListWithOptions(FORM_ID.USERS, options).subscribe({
      next: (res: any) => {
        const records = res?.data?.records || [];
        const secOps = this.getSecOpsRole();
        const newUsers = records.map((u: any) => {
          const id = u._id || u.id;
          const isUserSelected = this.isSelected(id);
          const assignedRole =
            this.userRolesMap.get(id) ||
            (isUserSelected ? (u.role || secOps.role) : u.role);
          const roleName =
            typeof assignedRole === 'object' && assignedRole !== null
              ? assignedRole?.role || assignedRole?.name || assignedRole?.roleName
              : assignedRole;
          const roleObj =
            typeof assignedRole === 'object' && assignedRole !== null
              ? assignedRole
              : this.userRolesObjectMap.get(id) ||
                this.roleRecords.find(
                  (r: any) =>
                    (r.role || r.name || r.roleName) === roleName,
                ) ||
                (isUserSelected && roleName === secOps.role
                  ? secOps.roleObject
                  : null);
          return {
            ...u,
            id,
            name: u.name,
            email: u.email,
            initials: u.initials || this.getInitials(u.name),
            status: u.status,
            profilePhoto: u.profilePhoto || u.userProfilePhoto,
            role: roleName,
            roleObject: roleObj,
          };
        });

        console.log('API Response records count:', records.length);

        if (append) {
          const existingIds = new Set(
            this.users.map((u) => u.id).filter(Boolean),
          );
          const uniqueNewUsers = newUsers.filter(
            (u: any) => !u.id || !existingIds.has(u.id),
          );
          console.log(
            'Appending users. New unique count:',
            uniqueNewUsers.length,
          );
          this.users = [...this.users, ...uniqueNewUsers];
        } else {
          this.users = newUsers;
        }

        // Keep selectedUserMap updated for any already selected users
        this.users.forEach((u) => {
          if (this.selectedUserIds.has(u.id)) {
            if (!u.role) {
              u.role = secOps.role;
              u.roleObject = secOps.roleObject;
            }
            this.selectedUserMap.set(u.id, u);
            if (u.role) {
              this.userRolesMap.set(u.id, u.role);
            }
            if (u.roleObject) {
              this.userRolesObjectMap.set(u.id, u.roleObject);
            }
          }
        });

        console.log('Total users in list:', this.users.length);

        this.totalRecords = res?.data?.totalCount || 0;

        if (newUsers.length === 0 || newUsers.length < this.limit) {
          this.hasMoreUsers = false;
        } else {
          this.hasMoreUsers =
            this.totalRecords > 0
              ? this.users.length < this.totalRecords
              : true;
        }

        this.isLoadingMore = false;
        this.isSearching = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching users for invite:', err);
        this.isLoadingMore = false;
        this.isSearching = false;
        this.cdr.detectChanges();
      },
    });
  }

  onScroll(event: any): void {
    const element = event.target;
    // Check if user scrolled to bottom of user-list container
    const atBottom =
      element.scrollHeight - element.scrollTop <= element.clientHeight + 50;

    if (atBottom && !this.isLoadingMore && this.hasMoreUsers) {
      this.isLoadingMore = true;
      this.currentPage++;
      this.loadUsersFromApi(true);
    }
  }

  onRoleDropdownScroll(event: any): void {
    const element = event.target;
    const atBottom =
      element.scrollHeight - element.scrollTop <= element.clientHeight + 10;

    if (atBottom && !this.isLoadingRoles && this.hasMoreRoles) {
      this.rolePage++;
      this.loadRolesFromApi(true);
    }
  }

  onKeyUpSearch(value: string): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTerm = value;
    const term = value.trim();
    if (term.length >= 3 || (term.length === 0 && this.users.length > 0)) {
      this.isSearching = true;
    }
    this.searchTimeout = setTimeout(() => {
      if (term.length >= 3 || term.length === 0) {
        this.loadUsersFromApi(false);
      } else {
        this.isSearching = false;
        this.cdr.detectChanges();
      }
    }, 350);
  }

  clearSearch(input: HTMLInputElement): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    input.value = '';
    this.searchTerm = '';
    this.isSearching = true;
    this.loadUsersFromApi(false);
  }

  toggleStatusFilter(event: MouseEvent): void {
    event.stopPropagation();
    this.isStatusFilterOpen = !this.isStatusFilterOpen;
    this.activeRoleDropdownUserId = null;
  }

  selectStatusFilter(filter: string, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedStatusFilter = filter;
    this.isStatusFilterOpen = false;
  }

  toggleRowRoleDropdown(
    userId: string | number,
    event: MouseEvent,
    index?: number,
  ): void {
    event.stopPropagation();
    if (this.activeRoleDropdownUserId === userId) {
      this.activeRoleDropdownUserId = null;
      this.dropdownDirectionMap.delete(userId);
    } else {
      this.activeRoleDropdownUserId = userId;
      this.roleSearchQuery = '';

      const usersList = this.filteredUsers || [];
      const idx =
        typeof index === 'number'
          ? index
          : usersList.findIndex((u) => u.id === userId);
      const isLastItem = idx > 0 && idx === usersList.length - 1;
      const isNearBottom =
        usersList.length >= 3 && idx >= usersList.length - 2;

      let openUpwards = false;
      const buttonEl = event.currentTarget as HTMLElement;

      if (buttonEl) {
        const containerEl = buttonEl.closest('.user-list') as HTMLElement;
        if (containerEl) {
          const containerRect = containerEl.getBoundingClientRect();
          const buttonRect = buttonEl.getBoundingClientRect();
          const spaceBelow = containerRect.bottom - buttonRect.bottom;
          const spaceAbove = buttonRect.top - containerRect.top;

          // If less than 220px below and there is space above, or if it's the last card
          if (spaceBelow < 220 && spaceAbove > 140) {
            openUpwards = true;
          } else if (isLastItem) {
            openUpwards = true;
          } else if (spaceBelow < 180) {
            openUpwards = true;
          }
        } else {
          const spaceBelow =
            window.innerHeight - buttonEl.getBoundingClientRect().bottom;
          openUpwards = spaceBelow < 240 || isLastItem;
        }
      } else {
        openUpwards = isLastItem || isNearBottom;
      }

      this.dropdownDirectionMap.set(userId, openUpwards);
    }
    this.isStatusFilterOpen = false;
  }

  isDropdownUpwards(user: UserInviteItem, index: number): boolean {
    if (this.dropdownDirectionMap.has(user.id)) {
      return this.dropdownDirectionMap.get(user.id)!;
    }
    const usersList = this.filteredUsers || [];
    return (
      (index > 0 && index === usersList.length - 1) ||
      (usersList.length >= 3 && index >= usersList.length - 2)
    );
  }

  selectRoleForUser(
    user: UserInviteItem,
    role: string | any,
    event?: MouseEvent,
  ): void {
    if (event) event.stopPropagation();
    const roleName =
      typeof role === 'object' && role !== null
        ? role.role || role.name || role.roleName
        : role;
    const roleObj =
      typeof role === 'object' && role !== null
        ? role
        : this.roleRecords.find(
            (r: any) => (r.role || r.name || r.roleName) === roleName,
          ) || null;

    user.role = roleName;
    user.roleObject = roleObj;
    this.userRolesMap.set(user.id, roleName);
    if (roleObj) {
      this.userRolesObjectMap.set(user.id, roleObj);
    } else {
      this.userRolesObjectMap.delete(user.id);
    }

    const inList = this.users.find((u) => u.id === user.id);
    if (inList) {
      inList.role = roleName;
      inList.roleObject = roleObj;
    }

    if (!this.isSelected(user.id)) {
      this.selectedUserIds.add(user.id);
    }
    this.selectedUserMap.set(user.id, {
      ...user,
      role: roleName,
      roleObject: roleObj,
    });
    this.activeRoleDropdownUserId = null;
    this.roleSearchQuery = '';
    this.roleError = false;
    this.syncFormGroupRole();
    this.emitSelectionChange();
  }

  // Master Checkbox & Select All across pages
  isCurrentPageAllSelected(): boolean {
    if (!this.filteredUsers || this.filteredUsers.length === 0) return false;
    return this.filteredUsers.every((u) => this.selectedUserIds.has(u.id));
  }

  isCurrentPageIndeterminate(): boolean {
    if (!this.filteredUsers || this.filteredUsers.length === 0) return false;
    const selectedCount = this.filteredUsers.filter((u) =>
      this.selectedUserIds.has(u.id),
    ).length;
    return selectedCount > 0 && selectedCount < this.filteredUsers.length;
  }

  toggleSelectAllCurrentPage(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectAllAcrossPages = false;
    const secOps = this.getSecOpsRole();

    if (checked) {
      this.filteredUsers.forEach((user) => {
        this.selectedUserIds.add(user.id);
        const role = this.userRolesMap.get(user.id) || user.role || secOps.role;
        const roleObj =
          this.userRolesObjectMap.get(user.id) ||
          user.roleObject ||
          this.roleRecords.find(
            (r: any) => (r.role || r.name || r.roleName) === role,
          ) ||
          (role === secOps.role ? secOps.roleObject : null);

        user.role = role;
        user.roleObject = roleObj;
        this.userRolesMap.set(user.id, role);
        if (roleObj) {
          this.userRolesObjectMap.set(user.id, roleObj);
        }
        const inList = this.users.find((u) => u.id === user.id);
        if (inList) {
          inList.role = role;
          inList.roleObject = roleObj;
        }
        this.selectedUserMap.set(user.id, {
          ...user,
          role,
          roleObject: roleObj,
        });
      });
    } else {
      this.filteredUsers.forEach((user) => {
        this.selectedUserIds.delete(user.id);
        this.selectedUserMap.delete(user.id);
        this.userRolesMap.delete(user.id);
        this.userRolesObjectMap.delete(user.id);
        user.role = undefined;
        user.roleObject = undefined;
        const inList = this.users.find((u) => u.id === user.id);
        if (inList) {
          inList.role = undefined;
          inList.roleObject = undefined;
        }
      });
    }

    if (this.selectedUserIds.size === 0) {
      this.roleError = false;
    }
    this.syncFormGroupRole();
    this.emitSelectionChange();
  }

  selectAllUsersAcrossAllPages(): void {
    const secOps = this.getSecOpsRole();

    if (this.totalRecords > this.users.length) {
      this.isLoadingMore = true;
      const options: any = {
        page: 1,
        limit: this.totalRecords > 0 ? this.totalRecords : 500,
        status: ['IN-ACTIVE'],
        contactType: ['Primary', 'Others'],
        userType: 'Member',
        sort: 'email',
      };

      if (this.searchTerm.trim()) {
        options.query = this.searchTerm.trim();
      }

      this.commonService.loadListWithOptions(FORM_ID.USERS, options).subscribe({
        next: (res: any) => {
          const records = res?.data?.records || [];
          const allNewUsers = records.map((u: any) => {
            const id = u._id || u.id;
            const assignedRole =
              this.userRolesMap.get(id) || u.role || secOps.role;
            const roleObj =
              this.userRolesObjectMap.get(id) ||
              u.roleObject ||
              this.roleRecords.find(
                (r: any) => (r.role || r.name || r.roleName) === assignedRole,
              ) ||
              (assignedRole === secOps.role ? secOps.roleObject : null);
            return {
              ...u,
              id,
              name: u.name,
              email: u.email,
              initials: u.initials || this.getInitials(u.name),
              status: u.status,
              profilePhoto: u.profilePhoto || u.userProfilePhoto,
              role: assignedRole,
              roleObject: roleObj,
            };
          });

          this.users = allNewUsers;
          this.totalRecords = res?.data?.totalCount || allNewUsers.length;

          allNewUsers.forEach((u: any) => {
            this.selectedUserIds.add(u.id);
            if (u.role) {
              this.userRolesMap.set(u.id, u.role);
            }
            if (u.roleObject) {
              this.userRolesObjectMap.set(u.id, u.roleObject);
            }
            this.selectedUserMap.set(u.id, u);
          });

          this.selectAllAcrossPages = true;
          this.hasMoreUsers = false;
          this.isLoadingMore = false;
          this.syncFormGroupRole();
          this.emitSelectionChange();
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error fetching all users for invite:', err);
          this.isLoadingMore = false;
          this.filteredUsers.forEach((u) => {
            this.selectedUserIds.add(u.id);
            if (!u.role) {
              u.role = secOps.role;
              u.roleObject = secOps.roleObject;
            }
            this.selectedUserMap.set(u.id, u);
          });
          this.selectAllAcrossPages = true;
          this.syncFormGroupRole();
          this.emitSelectionChange();
          this.cdr.detectChanges();
        },
      });
    } else {
      this.users.forEach((u) => {
        this.selectedUserIds.add(u.id);
        if (!u.role) {
          u.role = this.userRolesMap.get(u.id) || secOps.role;
          u.roleObject =
            this.userRolesObjectMap.get(u.id) ||
            this.roleRecords.find(
              (r: any) => (r.role || r.name || r.roleName) === u.role,
            ) ||
            (u.role === secOps.role ? secOps.roleObject : null);
        }
        if (u.role) {
          this.userRolesMap.set(u.id, u.role);
        }
        if (u.roleObject) {
          this.userRolesObjectMap.set(u.id, u.roleObject);
        }
        this.selectedUserMap.set(u.id, u);
      });
      this.selectAllAcrossPages = true;
      this.syncFormGroupRole();
      this.emitSelectionChange();
    }
  }

  getStatusPillClass(status?: string): string {
    if (!status) return 'status-pill-inactive';
    const s = status.toUpperCase().replace('-', '');
    if (s === 'ACTIVE') return 'status-pill-active';
    if (s.includes('INVITE')) return 'status-pill-invited';
    if (s.includes('SUSPEND') || s.includes('DELETE')) return 'status-pill-danger';
    if (s.includes('ARCHIV')) return 'status-pill-archived';
    return 'status-pill-inactive';
  }

  getDisplayStatus(status?: string): string {
    if (!status) return 'IN-ACTIVE';
    const s = status.toUpperCase();
    if (s === 'INACTIVE' || s === 'IN-ACTIVE') return 'IN-ACTIVE';
    return s;
  }

  get filteredUsers(): UserInviteItem[] {
    if (this.selectedStatusFilter === 'All Users') {
      return this.users;
    }
    const filter = this.selectedStatusFilter.toLowerCase();
    return this.users.filter((user) => {
      const status = (user.status || '').toLowerCase().replace('-', '');
      if (filter === 'inactive') {
        return status.includes('inactive');
      }
      if (filter === 'active') {
        return status === 'active';
      }
      if (filter === 'invited') {
        return status.includes('invite');
      }
      return status.includes(filter);
    });
  }

  get selectedUsers(): UserInviteItem[] {
    const list: UserInviteItem[] = [];
    const seen = new Set<string | number>();
    const secOps = this.getSecOpsRole();

    // 1. Check loaded users
    for (const u of this.users) {
      if (this.selectedUserIds.has(u.id) && !seen.has(u.id)) {
        seen.add(u.id);
        const role =
          this.userRolesMap.get(u.id) || u.role || secOps.role;
        const roleObject =
          this.userRolesObjectMap.get(u.id) ||
          u.roleObject ||
          this.roleRecords.find(
            (r: any) => (r.role || r.name || r.roleName) === role,
          ) ||
          (role === secOps.role ? secOps.roleObject : null);
        list.push({ ...u, role, roleObject });
      }
    }

    // 2. Also check selectedUserMap in case user was paged out
    for (const [id, u] of this.selectedUserMap.entries()) {
      if (this.selectedUserIds.has(id) && !seen.has(id)) {
        seen.add(id);
        const role =
          this.userRolesMap.get(id) || u.role || secOps.role;
        const roleObject =
          this.userRolesObjectMap.get(id) ||
          u.roleObject ||
          this.roleRecords.find(
            (r: any) => (r.role || r.name || r.roleName) === role,
          ) ||
          (role === secOps.role ? secOps.roleObject : null);
        list.push({ ...u, role, roleObject });
      }
    }

    return list;
  }

  isSelected(userId: string | number): boolean {
    return this.selectedUserIds.has(userId);
  }

  onSelectRole(role: string): void {
    this.selectedRole = role;
    this.selectedRoleObject =
      this.roleRecords.find(
        (r: any) => (r.role || r.name || r.roleName) === role,
      ) || { role: role, name: role };
    this.isRoleDropdownOpen = false;
    this.roleError = false;
    this.syncFormGroupRole();
  }

  toggleUser(user: UserInviteItem): void {
    if (this.selectedUserIds.has(user.id)) {
      this.selectedUserIds.delete(user.id);
      this.selectedUserMap.delete(user.id);
      this.userRolesMap.delete(user.id);
      this.userRolesObjectMap.delete(user.id);
      this.selectAllAcrossPages = false;

      user.role = undefined;
      user.roleObject = undefined;
      const inList = this.users.find((u) => u.id === user.id);
      if (inList) {
        inList.role = undefined;
        inList.roleObject = undefined;
      }
    } else {
      this.selectedUserIds.add(user.id);

      const secOps = this.getSecOpsRole();
      const assignedRole = this.userRolesMap.get(user.id) || secOps.role;
      const assignedRoleObj =
        this.userRolesObjectMap.get(user.id) ||
        this.roleRecords.find(
          (r: any) => (r.role || r.name || r.roleName) === assignedRole,
        ) ||
        (assignedRole === secOps.role ? secOps.roleObject : null);

      user.role = assignedRole;
      user.roleObject = assignedRoleObj;
      this.userRolesMap.set(user.id, assignedRole);
      if (assignedRoleObj) {
        this.userRolesObjectMap.set(user.id, assignedRoleObj);
      }

      const inList = this.users.find((u) => u.id === user.id);
      if (inList) {
        inList.role = assignedRole;
        inList.roleObject = assignedRoleObj;
      }

      this.selectedUserMap.set(user.id, {
        ...user,
        role: assignedRole,
        roleObject: assignedRoleObj,
      });
    }
    if (this.selectedUserIds.size === 0) {
      this.roleError = false;
    }
    this.syncFormGroupRole();
    this.emitSelectionChange();
  }

  removeUser(user: UserInviteItem): void {
    this.selectedUserIds.delete(user.id);
    this.selectedUserMap.delete(user.id);
    this.userRolesMap.delete(user.id);
    this.userRolesObjectMap.delete(user.id);
    this.selectAllAcrossPages = false;

    user.role = undefined;
    user.roleObject = undefined;
    const existing = this.users.find((u) => u.id === user.id);
    if (existing) {
      existing.role = undefined;
      existing.roleObject = undefined;
    }
    if (this.selectedUserIds.size === 0) {
      this.roleError = false;
    }
    this.syncFormGroupRole();
    this.emitSelectionChange();
  }

  clearSelection(): void {
    this.selectedUserIds.clear();
    this.selectedUserMap.clear();
    this.selectAllAcrossPages = false;
    this.users.forEach((u) => {
      u.role = undefined;
      u.roleObject = undefined;
    });
    this.userRolesMap.clear();
    this.userRolesObjectMap.clear();
    this.roleError = false;
    this.syncFormGroupRole();
    this.clearSelectionEvent.emit();
    this.emitSelectionChange();
  }

  syncFormGroupRole(): void {
    if (!this.formGroup) return;
    const roles = this.selectedUsers.map((u) => u.role).filter(Boolean);
    const secOps = this.getSecOpsRole();
    const primaryRole =
      roles.length > 0
        ? roles[0]
        : secOps.role;

    if (this.formGroup.contains('role')) {
      this.formGroup.get('role')?.setValue(primaryRole);
    }
    if (this.formGroup.contains('selectedUsers')) {
      this.formGroup.get('selectedUsers')?.setValue(this.selectedUsers);
    }
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  getAvatarClass(name: string): string {
    if (!name) return 'avatar-color-0';
    let code = 0;
    for (let i = 0; i < name.length; i++) {
      code += name.charCodeAt(i);
    }
    return `avatar-color-${code % 9}`;
  }

  submitInvitation(): void {
    this.roleError = false;

    const secOps = this.getSecOpsRole();

    // Ensure every selected user has a role assigned
    this.selectedUsers.forEach((u) => {
      if (!u.role) {
        u.role = this.userRolesMap.get(u.id) || secOps.role;
        this.userRolesMap.set(u.id, u.role);
      }
      if (!u.roleObject) {
        const found = this.roleRecords.find(
          (r: any) => (r.role || r.name || r.roleName) === u.role,
        );
        u.roleObject =
          found ||
          this.userRolesObjectMap.get(u.id) ||
          (u.role === secOps.role ? secOps.roleObject : null);
        if (u.roleObject) {
          this.userRolesObjectMap.set(u.id, u.roleObject);
        }
      }
    });

    const rolesToSend = this.selectedUsers.map(
      (u: any) => u.roleObject || u.role,
    );

    this.syncFormGroupRole();

    this.sendInvitation.emit({
      selectedUsers: this.selectedUsers.map((u: any) => ({
        ...u,
        role: u.roleObject || u.role,
        roleObject: u.roleObject,
      })),
      role: rolesToSend,
    });
  }

  private emitSelectionChange(): void {
    this.selectionChange.emit(this.selectedUsers);
  }
}
